/**
 * Operations Assignment Layer — assign scorekeepers and refs to games.
 *
 * Reads role assignments (commissioner+staff+scorekeeper+referee) from the
 * directory and lets the commissioner pick people per game with conflict
 * detection (same person, overlapping slots).
 */

import type { GameAssignment, Id, Profile } from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso } from './ids';
import { listScheduleSlots } from './schedulerService';

export type CrewPosition = GameAssignment['position'];

export function listGameAssignments(tournamentId: Id): GameAssignment[] {
  const db = getDb();
  const tournamentGameIds = new Set(
    db.games.filter((g) => g.tournamentId === tournamentId).map((g) => g.id),
  );
  return db.gameAssignments.filter((a) => tournamentGameIds.has(a.gameId));
}

export function listAssignmentsForGame(gameId: Id): GameAssignment[] {
  return getDb().gameAssignments.filter((a) => a.gameId === gameId);
}

/** Find profiles eligible for a crew position based on directory roles. */
export function eligibleCrewProfiles(position: CrewPosition): Profile[] {
  const db = getDb();
  return db.profiles.filter((p) => {
    if (position === 'scorekeeper') return p.globalRoles.some((r) => r === 'scorekeeper' || r === 'admin' || r === 'staff');
    if (position === 'referee_main' || position === 'referee_linesman')
      return p.globalRoles.some((r) => r === 'referee' || r === 'admin');
    if (position === 'timekeeper' || position === 'staff')
      return p.globalRoles.some((r) => r === 'staff' || r === 'admin');
    return false;
  });
}

export interface CrewAssignmentInput {
  gameId: Id;
  profileId: Id;
  position: CrewPosition;
}

export function assignCrew(input: CrewAssignmentInput): GameAssignment {
  const ts = nowIso();
  let assignment: GameAssignment | undefined;
  updateDb((db) => {
    const existing = db.gameAssignments.find(
      (a) => a.gameId === input.gameId && a.profileId === input.profileId && a.position === input.position,
    );
    if (existing) {
      assignment = existing;
      return db;
    }
    assignment = {
      id: newId('ga'),
      kind: 'game-assignment',
      createdAt: ts,
      updatedAt: ts,
      gameId: input.gameId,
      profileId: input.profileId,
      position: input.position,
      status: 'invited',
    };
    return { ...db, gameAssignments: [...db.gameAssignments, assignment] };
  });
  return assignment!;
}

export function unassignCrew(assignmentId: Id): void {
  updateDb((db) => ({
    ...db,
    gameAssignments: db.gameAssignments.filter((a) => a.id !== assignmentId),
  }));
}

export function setAssignmentStatus(assignmentId: Id, status: GameAssignment['status']): void {
  updateDb((db) => ({
    ...db,
    gameAssignments: db.gameAssignments.map((a) =>
      a.id === assignmentId ? { ...a, status, updatedAt: nowIso() } : a,
    ),
  }));
}

/** Auto-fill scorekeepers + main refs for any game missing them. */
export function autoAssignCrew(tournamentId: Id): { assigned: number } {
  const db = getDb();
  const games = db.games.filter((g) => g.tournamentId === tournamentId);
  const slots = new Map(listScheduleSlots(tournamentId).map((s) => [s.id, s]));
  const scorekeepers = eligibleCrewProfiles('scorekeeper');
  const refs = eligibleCrewProfiles('referee_main');
  let assigned = 0;

  // Track per-profile occupied windows (epoch ms ranges).
  const busy = new Map<Id, Array<[number, number]>>();
  for (const a of db.gameAssignments) {
    const game = db.games.find((g) => g.id === a.gameId);
    const slot = game?.scheduleSlotId ? slots.get(game.scheduleSlotId) : null;
    if (!slot) continue;
    const range: [number, number] = [
      new Date(slot.startsAt).getTime(),
      new Date(slot.endsAt).getTime(),
    ];
    const list = busy.get(a.profileId) ?? [];
    list.push(range);
    busy.set(a.profileId, list);
  }

  const overlaps = (profileId: Id, range: [number, number]) =>
    (busy.get(profileId) ?? []).some(([a, b]) => !(range[1] <= a || range[0] >= b));

  const pickAvailable = (pool: Profile[], range: [number, number]): Profile | undefined => {
    // Round-robin-ish: prefer the person with fewest games already.
    const counts = new Map<Id, number>();
    pool.forEach((p) => counts.set(p.id, (busy.get(p.id) ?? []).length));
    return pool
      .filter((p) => !overlaps(p.id, range))
      .sort((a, b) => (counts.get(a.id) ?? 0) - (counts.get(b.id) ?? 0))[0];
  };

  for (const game of games) {
    const slot = game.scheduleSlotId ? slots.get(game.scheduleSlotId) : null;
    if (!slot) continue;
    const range: [number, number] = [
      new Date(slot.startsAt).getTime(),
      new Date(slot.endsAt).getTime(),
    ];
    const existing = db.gameAssignments.filter((a) => a.gameId === game.id);
    if (!existing.some((a) => a.position === 'scorekeeper')) {
      const sk = pickAvailable(scorekeepers, range);
      if (sk) {
        assignCrew({ gameId: game.id, profileId: sk.id, position: 'scorekeeper' });
        const list = busy.get(sk.id) ?? [];
        list.push(range);
        busy.set(sk.id, list);
        assigned += 1;
      }
    }
    if (!existing.some((a) => a.position === 'referee_main')) {
      const r = pickAvailable(refs, range);
      if (r) {
        assignCrew({ gameId: game.id, profileId: r.id, position: 'referee_main' });
        const list = busy.get(r.id) ?? [];
        list.push(range);
        busy.set(r.id, list);
        assigned += 1;
      }
    }
  }

  return { assigned };
}

/** Detect crew double-bookings across the tournament. */
export interface CrewConflict {
  profileId: Id;
  gameAId: Id;
  gameBId: Id;
}

export function detectCrewConflicts(tournamentId: Id): CrewConflict[] {
  const db = getDb();
  const slots = new Map(listScheduleSlots(tournamentId).map((s) => [s.id, s]));
  const games = db.games.filter((g) => g.tournamentId === tournamentId);
  const byProfile = new Map<Id, Array<{ gameId: Id; range: [number, number] }>>();
  for (const a of db.gameAssignments) {
    const game = games.find((g) => g.id === a.gameId);
    const slot = game?.scheduleSlotId ? slots.get(game.scheduleSlotId) : null;
    if (!slot || !game) continue;
    const list = byProfile.get(a.profileId) ?? [];
    list.push({
      gameId: game.id,
      range: [new Date(slot.startsAt).getTime(), new Date(slot.endsAt).getTime()],
    });
    byProfile.set(a.profileId, list);
  }
  const out: CrewConflict[] = [];
  for (const [profileId, list] of byProfile.entries()) {
    list.sort((a, b) => a.range[0] - b.range[0]);
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i];
      const b = list[i + 1];
      if (b.range[0] < a.range[1]) {
        out.push({ profileId, gameAId: a.gameId, gameBId: b.gameId });
      }
    }
  }
  return out;
}
